import {
    Component,
    ChangeDetectionStrategy,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectorRef,
} from '@angular/core';
import { Iri } from '../../../core/dto/iri';
import { ContentService } from '../../../core/api/content.service';
import { slug } from '../../../core/slug';
import { typeFromIri } from '../../../core/type-from-iri';

export interface ContentDto {
    '@id': string;
    '@type': string;
    title?: string;
    name?: string;
    fullNameOfDataSet?: string;
    externalLink?: string;
}

export interface AssociatedContentDto {
    '@id': string;
    type: string;
    title: string;
    externalLink?: string;
}

@Component({
    selector: 'isav-people-associated-content',
    templateUrl: './people-associated-content.html',
    styleUrls: ['./people-associated-content.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeopleAssociatedContent implements OnChanges {
    @Input() iri: Iri;

    contents: ContentDto[];
    data: AssociatedContentDto[];
    loading: boolean = false;
    shownContents: number = 10;

    orderBy: 'title' | 'type' = 'title';
    sortBy: 'asc' | 'desc' = 'asc';
    all: boolean = false;

    constructor(private contentService: ContentService, private cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.iri) {
            this.loadContents();
        }
    }

    viewLink(data: AssociatedContentDto) {
        if (data.type === 'HowToGuide') return ['/how-to-guide'];
        return [`/${typeFromIri(data['@id']).replace(/_/g, '-')}/view`, slug(data.title || '')];
    }

    loadContents() {
        this.loading = true;
        this.contentService
            .find<ContentDto>({
                'owners.id': this.iri,
                pagination: '0',
            })
            .subscribe((contents) => {
                this.contents = contents;
                this.loadData();
                this.loading = false;
            });
    }

    sort(orderBy) {
        if (this.orderBy === orderBy) {
            this.sortBy = this.sortBy !== 'asc' ? 'asc' : 'desc';
        } else {
            this.orderBy = orderBy;
            this.sortBy = 'asc';
        }
        this.loadData();
    }

    showAll(amount: number) {
        this.shownContents = amount;
        this.all = true;
        this.loadData();
    }

    hide() {
        this.shownContents = 10;
        this.all = false;
        this.loadData();
    }

    loadData() {
        if (this.sortBy === 'asc') {
            this.data = this.all
                ? this.sortContents()
                : this.sortContents().slice(0, this.shownContents);
        } else {
            this.data = this.all
                ? this.sortContents().reverse()
                : this.sortContents().reverse().slice(0, this.shownContents);
        }
        this.cdr.markForCheck();
    }

    // Sorting on frontend side, because API could not handle contents with different title property
    sortContents(): AssociatedContentDto[] {
        return this.contents
            .map((content) => {
                return {
                    '@id': content['@id'],
                    type: content['@type'],
                    title:
                        content.title ||
                        content.name ||
                        content.fullNameOfDataSet ||
                        content['@id'],
                    externalLink: content.externalLink,
                };
            })
            .sort((a, b) => a[this.orderBy].localeCompare(b[this.orderBy]));
    }
}
